// Fuse.js options for residence filter search
export const FUSE_OPTIONS = {
  includeScore: true,
  threshold: 0.0,
  keys: [
    'bedroom_data',
    'floor_plan',
    'level_of_care',
    'map_building_grouping',
    'residence_status',
    'floor_plan_category',
  ],
};

/**
 * Processes category data by removing unnecessary fields
 * @param {Object} locData - Location data object
 * @param {boolean} platformShowOccupancy - Whether to show occupancy
 * @param {boolean} showFavoriteResidences - Whether to show favorite residences
 * @returns {Object|null} Processed category data
 */
export const processCategoryData = (locData, platformShowOccupancy, showFavoriteResidences) => {
  if (!locData) return null;

  const processedData = { ...locData };

  // Remove the data we don't need for the category pane
  delete processedData.level_of_care;

  if (!platformShowOccupancy) {
    delete processedData.residence_status;
  }

  if (!showFavoriteResidences) {
    delete processedData.favorite_residences;
  }

  return processedData;
};

/**
 * Checks if active filter selections have any filters
 * @param {Object} activeFilterSelections - Active filter selections object
 * @returns {boolean} Whether there are active filters
 */
export const hasActiveFilters = (activeFilterSelections) => Object.keys(activeFilterSelections).length > 0;

/**
 * Processes changed unit data for Fuse index update
 * @param {Object} changedUnit - Changed unit object
 * @param {Array} residenceStatusOptions - Available status options
 * @returns {Object|null} Processed unit data or null if invalid
 */
export const processChangedUnit = (changedUnit, residenceStatusOptions) => {
  const changedUnitSvgId = changedUnit?.data?.attributes?.field_svg_id;
  const changedUnitRelationships = changedUnit?.data?.relationships;

  if (!changedUnitSvgId) return null;

  const changedUnitStatusUuid = changedUnitRelationships?.field_status?.data?.id;
  const changedUnitStatusPhrase = residenceStatusOptions.find(
    (status) => status.id === changedUnitStatusUuid,
  );

  if (!changedUnitStatusPhrase) return null;

  return {
    svgId: changedUnitSvgId,
    status: `${changedUnitStatusUuid} - ${changedUnitStatusPhrase.name}`,
  };
};

/**
 * Compares two objects for deep equality (for filter selections)
 * @param {Object} obj1 - First object
 * @param {Object} obj2 - Second object
 * @returns {boolean} Whether objects are equal
 */
export const areObjectsEqual = (obj1, obj2) => JSON.stringify(obj1) === JSON.stringify(obj2);

/**
  * Helper function to get UUIDs of a type within the results.
  *
  * @param {string} type
  *   A Category type string. E.g. 'location', 'floor_plan', etc.
  *
  * @param {array} results
  *   Filtered Results set that you want to get values from.
  *
  * @returns {array}
  *   Array of unique UUIDs of a type within the Filtered Results.
  */
export const getFilterResultsByType = (type, results) => results.map((result) => {
  // When the results are from Fusejs, they have an 'item' property,
  // so normalize this so that this function works with original data
  // and filtered data.
  let returnResult = result;
  if (Object.prototype.hasOwnProperty.call(result, 'item')) {
    returnResult = result.item;
  }

  // Split the string up so we return only the UUID.
  // Return null if the type doesn't exist or is null
  return returnResult[type]?.uuid || null;
})
  .filter((result) => result !== null) // Remove null values
  .filter((result, index, allResults) => allResults.indexOf(result) === index);
// Filter so we only have unique values in the array.

export const updatePendingSelections = (pendingSelections, activeCategoryFilter, detailData) => {
  const {
    uuid,
    keyColor,
    label,
  } = detailData;

  const newPendingSelections = { ...pendingSelections };

  // Initialize the category if it doesn't exist
  if (!newPendingSelections[activeCategoryFilter]) {
    newPendingSelections[activeCategoryFilter] = {};
  }

  // Toggle the selection
  if (newPendingSelections[activeCategoryFilter][uuid]) {
    // If already selected, remove it
    delete newPendingSelections[activeCategoryFilter][uuid];
    // If category is now empty, remove it
    if (Object.keys(newPendingSelections[activeCategoryFilter]).length === 0) {
      delete newPendingSelections[activeCategoryFilter];
    }
  } else if (activeCategoryFilter === 'floor_plan') {
    // If not selected, add it with color information for floor plans
    newPendingSelections[activeCategoryFilter][uuid] = {
      uuid,
      label,
      color: keyColor,
    };
  } else {
    newPendingSelections[activeCategoryFilter][uuid] = {
      uuid,
      label,
    };
  }

  return newPendingSelections;
};

export const createDetailBtnStyle = (data, activeCategoryFilter) => {
  const color = data.keyColor || '#000000';
  let style = {};
  if (activeCategoryFilter && activeCategoryFilter === 'floor_plan') {
    style = {
      backgroundColor: color,
      borderColor: color,
    };
  }
  return style;
};
